
/**
 * Model configuration registry — Single source of truth for all AI model definitions.
 *
 * add new models by appending to MODEL_OPTIONS
 */

export interface ModelConfig {
  /** Unique identifier used in Redux state and UI selection */
  id: string;
  /** User-facing display name (used as fallback) */
  displayName: string;
  /** Short model name shown on the first line of the selector (e.g. "GPT 5.4") */
  shortName?: string;
  /** Capability description shown on the second line (e.g. "Fast and capable") */
  description?: string;
  /** Model value sent to the backend API */
  modelValue: string;
  /** Default query_type for this model */
  queryType: string;
  /** If set, selecting this model should also activate the given tool. */
  activatesTool?: string | null;
  /** When false, this model stays out of the selector UI. */
  visibleInSelector?: boolean;
  /** If set, this model is automatically used when this tool is active (overrides user selection) */
  defaultForTool?: string;
  /** Icon representing the model in the UI */
  icon: React.ReactNode;
}

export type Model = 'gpt-5.5-fast' | 'gpt-5.5-reasoning' | 'gpt-image-1' | 'gpt-image-2' | 'claude-sonnet-5.0-fast' | 'claude-opus-4.8-reasoning' | 'amazon-nova-lite';

/** Resolved configuration after applying tool-specific overrides */
export interface ResolvedModelConfig {
  modelValue: string;
  queryType: string;
}

export const DEFAULT_STANDARD_MODEL_ID = 'gpt-5.5-fast';
export const DEFAULT_REASONING_MODEL_ID = 'gpt-5.5-reasoning';
export const DEFAULT_IMAGE_MODEL_ID = 'gpt-image-1';
export const WEB_SEARCH_MODEL_ID = 'amazon-nova-lite';

/** Maps deprecated model IDs to their current equivalents. */
const MODEL_ID_MIGRATIONS: Record<string, string> = {
  'gpt-5.4-fast': DEFAULT_STANDARD_MODEL_ID,
  'gpt-5.4-reasoning': DEFAULT_REASONING_MODEL_ID,
  'claude-sonnet-4.6-fast': 'claude-sonnet-5.0-fast',
  'claude-opus-4.7-reasoning': 'claude-opus-4.8-reasoning',
};

/**
 * Returns the current model ID for a potentially deprecated one.
 * If the ID is already current, returns it unchanged.
 */
export function migrateModelId(modelId: string): string {
  return MODEL_ID_MIGRATIONS[modelId] ?? modelId;
}

const gptIcon = <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.05476 6.54828C0.917797 6.14224 0.847943 5.7171 0.847875 5.28917C0.847919 4.58106 1.03908 3.88573 1.40171 3.27467C2.13034 2.02319 3.48203 1.25055 4.94494 1.25055C5.23274 1.25055 5.52103 1.28055 5.80238 1.34014C6.18141 0.918774 6.64667 0.581467 7.16746 0.350466C7.68826 0.119465 8.25277 1.79484e-05 8.82377 0H8.84941C8.85204 8.78351e-05 8.85604 8.77869e-05 8.85902 8.77869e-05C10.6308 8.77869e-05 12.2021 1.12815 12.7468 2.79122C13.8874 3.02131 14.8721 3.72711 15.4478 4.72491C15.8093 5.33968 15.9998 6.03776 16 6.7485C15.9999 7.74736 15.6241 8.71062 14.9454 9.45176C15.0824 9.85779 15.1522 10.2829 15.1523 10.7109C15.1522 11.419 14.9611 12.1143 14.5985 12.7254C13.8699 13.9772 12.5181 14.7494 11.0551 14.7494C10.767 14.7494 10.4798 14.7194 10.1981 14.6598C9.81902 15.0812 9.35372 15.4185 8.83288 15.6495C8.31204 15.8805 7.74749 16 7.17645 16H7.15082L7.1404 16C5.36761 16 3.79685 14.8719 3.25213 13.2073C2.11152 12.9771 1.12686 12.2714 0.55117 11.2735C0.190056 10.6593 -8.96454e-05 9.96183 1.90735e-06 9.25176C0.000120163 8.25291 0.375905 7.28964 1.05458 6.5485L1.05476 6.54828ZM7.14975 14.954H7.15393C7.8633 14.9538 8.55016 14.7082 9.09506 14.2601C9.0627 14.2429 9.03072 14.225 8.99914 14.2065L5.77038 12.3663C5.68981 12.321 5.62282 12.2555 5.57621 12.1764C5.5296 12.0974 5.50503 12.0075 5.50501 11.9161V7.42167L4.14029 8.19919C4.12578 8.20635 4.1158 8.22022 4.11376 8.23608V11.9556C4.11563 13.6092 5.47367 14.9507 7.14975 14.954ZM13.6789 12.2026C13.9456 11.7476 14.0861 11.2313 14.0862 10.7057C14.0862 10.5342 14.0711 10.3623 14.0415 10.1934C14.0175 10.2076 13.9756 10.2328 13.9456 10.2499L10.7168 12.0901C10.6363 12.1364 10.5448 12.1607 10.4516 12.1607C10.3584 12.1607 10.2669 12.1363 10.1864 12.09L6.24441 9.84416V11.3992C6.24397 11.4071 6.24552 11.415 6.24892 11.4222C6.25232 11.4294 6.25747 11.4357 6.26391 11.4405L9.52783 13.2999C9.98969 13.5622 10.5132 13.7003 11.0461 13.7004C11.5795 13.7004 12.1035 13.5619 12.5656 13.299C13.0277 13.0361 13.4116 12.6579 13.6788 12.2024L13.6789 12.2026ZM14.5283 5.2478C14.1737 4.64003 13.6137 4.17467 12.9465 3.93317C12.9465 3.96062 12.9481 4.00919 12.9481 4.04296V7.7233L12.9481 7.72637C12.9481 7.81775 12.9236 7.9075 12.877 7.9865C12.8305 8.0655 12.7636 8.13094 12.6831 8.17618L8.74112 10.4217L10.1058 11.1992C10.1126 11.2036 10.1203 11.2062 10.1283 11.207C10.1364 11.2077 10.1444 11.2064 10.1519 11.2033L13.4161 9.34227C13.8776 9.07852 14.2607 8.69973 14.527 8.24386C14.7934 7.78799 14.9336 7.27105 14.9337 6.74486C14.9335 6.21948 14.7936 5.70333 14.5281 5.24793L14.5283 5.2478ZM3.31587 7.82229L7.25782 5.57648L5.89309 4.79927C5.88636 4.79488 5.87864 4.79221 5.87061 4.79149C5.86258 4.79077 5.85449 4.79202 5.84707 4.79514L2.58283 6.65456C1.64327 7.19014 1.06361 8.18052 1.06361 9.2515C1.06361 10.5077 1.85807 11.6318 3.05259 12.0656V8.27521C3.05241 8.2738 3.05241 8.27235 3.05241 8.27099C3.05241 8.0857 3.15305 7.91443 3.31587 7.82229ZM1.95751 5.80515C1.9815 5.79062 2.02339 5.76572 2.05343 5.74868L5.28219 3.90849C5.3627 3.86219 5.4542 3.83778 5.54738 3.83774C5.6405 3.83774 5.7321 3.86224 5.81253 3.90849L9.75452 6.1543V4.59927L9.75457 4.59659C9.75457 4.58909 9.7528 4.58169 9.7494 4.57498C9.746 4.56827 9.74106 4.56244 9.73498 4.55794L6.47106 2.7001C6.00937 2.43747 5.48582 2.29921 4.95291 2.29917C3.2747 2.29917 1.91371 3.64204 1.91371 5.29791C1.91381 5.46783 1.92847 5.63744 1.95751 5.80493V5.80515ZM10.4965 8.57675L11.8615 7.79923C11.8687 7.79572 11.8748 7.79051 11.8795 7.78407C11.8841 7.77764 11.887 7.77017 11.888 7.76234V4.04287C11.8873 2.38784 10.5263 1.04612 8.84883 1.04612C8.13837 1.04627 7.45037 1.29183 6.90423 1.74019C6.93655 1.75746 6.96853 1.77535 7.00015 1.79386L10.2289 3.634C10.3095 3.67923 10.3765 3.7447 10.4231 3.82375C10.4697 3.9028 10.4942 3.99262 10.4942 4.08407V4.08706L10.4965 8.57675ZM9.75515 6.99967L7.99944 5.99914L6.24379 6.99901V8.99945L7.99944 9.99932L9.75515 8.99945V6.99967Z" fill="currentColor"/>
</svg>;

const claudeIcon = <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clipPath="url(#clip0_5570_28033)">
<path fillRule="evenodd" clipRule="evenodd" d="M3.61287 0H12.3871C14.3742 0 16 1.63334 16 3.62961V12.3704C16 14.3667 14.3742 16 12.3871 16H3.61287C1.62581 16 0 14.3667 0 12.3704V3.62961C0 1.63334 1.62581 0 3.61287 0Z" fill="#D77655"/>
<path d="M4.44587 9.94051L6.74759 8.64309L6.78627 8.53042L6.74759 8.46775L6.63543 8.46772L6.25074 8.44389L4.93562 8.40817L3.79505 8.36057L2.69002 8.30105L2.41199 8.24155L2.15137 7.89637L2.17821 7.7242L2.41202 7.56629L2.74693 7.59564L3.48702 7.64641L4.59755 7.72338L5.40324 7.77101L6.59677 7.89559H6.78627L6.81315 7.81864L6.74837 7.77101L6.6978 7.72338L5.54855 6.94097L4.30449 6.11409L3.65287 5.63796L3.30059 5.39675L3.12287 5.17059L3.04627 4.67703L3.36618 4.32312L3.79587 4.35247L3.90565 4.38186L4.3409 4.71828L5.27055 5.44121L6.48455 6.33948L6.66227 6.48788L6.73337 6.43711L6.74205 6.40139L6.66227 6.2673L6.00196 5.06824L5.2974 3.84862L4.9838 3.34313L4.90087 3.04002C4.87165 2.91544 4.85034 2.81071 4.85034 2.68293L5.21446 2.18617L5.41587 2.12109L5.90165 2.18617L6.10624 2.36472L6.40796 3.05829L6.8969 4.1502L7.65518 5.63488L7.87715 6.07529L7.99562 6.48317L8.03987 6.60774L8.1164 6.60771V6.53629L8.1788 5.6999L8.29412 4.67308L8.40627 3.35186L8.44499 2.97971L8.62821 2.53371L8.99234 2.29251L9.27668 2.42901L9.51049 2.76544L9.47812 2.98288L9.33909 3.89066L9.06659 5.31265L8.88887 6.26488H8.9924L9.1109 6.14586L9.59034 5.50629L10.396 4.49453L10.7515 4.09303L11.1661 3.64945L11.4323 3.43835L11.9354 3.43832L12.3059 3.9914L12.14 4.56275L11.6219 5.22295L11.1922 5.78238L10.5761 6.61559L10.1914 7.28213L10.227 7.33532L10.3186 7.32659L11.7103 7.029L12.4623 6.89252L13.3596 6.73781L13.7656 6.92825L13.8098 7.12186L13.6503 7.51784L12.6906 7.75591L11.565 7.98208L9.8889 8.38045L9.86837 8.39552L9.89205 8.42487L10.6472 8.49629L10.9702 8.51375H11.7609L13.2332 8.62407L13.6178 8.87959L13.8485 9.19225L13.8098 9.43032L13.2174 9.73343L12.4181 9.54299L10.5524 9.09703L9.91255 8.93673L9.82412 8.9367V8.98988L10.3573 9.51361L11.3343 10.4L12.5578 11.5426L12.6202 11.8252L12.4631 12.0481L12.2972 12.0243L11.2222 11.2117L10.8075 10.846L9.86834 10.0516L9.80596 10.0516V10.1349L10.0224 10.4531L11.1653 12.179L11.2246 12.7083L11.1416 12.8805L10.8454 12.9845L10.52 12.925L9.85099 11.9815L9.16062 10.9189L8.60377 9.96669L8.53587 10.0056L8.20727 13.5614L8.05324 13.7431L7.6978 13.8796L7.40162 13.6534L7.24443 13.2876L7.40162 12.5647L7.59118 11.6212L7.74518 10.8713L7.88421 9.93969L7.96715 9.63021L7.96162 9.60958L7.89371 9.61831L7.19465 10.5824L6.13149 12.0259L5.29027 12.9305L5.08887 13.0107L4.73971 12.8289L4.77212 12.5044L4.96721 12.2156L6.13149 10.7277L6.83368 9.80561L7.28706 9.27312L7.2839 9.19617H7.25705L4.16474 11.2133L3.61418 11.2847L3.37721 11.0617L3.40646 10.6959L3.51862 10.5769L4.44827 9.93414L4.44512 9.93734L4.44587 9.94051Z" fill="#FCF2EE"/>
</g>
<defs>
<clipPath id="clip0_5570_28033">
<rect width="16" height="16" fill="white"/>
</clipPath>
</defs>
</svg>


/** All available models — add new entries here to make them available in the UI */
export const MODEL_OPTIONS: readonly ModelConfig[] = [
  {
    id: DEFAULT_STANDARD_MODEL_ID,
    displayName: 'GPT-5.5',
    shortName: 'GPT-5.5',
    description: 'Fast and capable',
    modelValue: import.meta.env.VITE_MODEL_GPT_5_5_CHAT || 'gpt-5.5',
    queryType: 'response-api',
    activatesTool: null,
    icon: gptIcon,
  },
  {
    id: DEFAULT_REASONING_MODEL_ID,
    displayName: 'GPT-5.5 (Reasoning)',
    shortName: 'GPT-5.5 (Reasoning)',
    description: 'Solves complex problems',
    modelValue: import.meta.env.VITE_MODEL_GPT_5_5_REASONING || 'gpt-5.5-reasoning',
    queryType: 'response-api',
    activatesTool: 'reasoning',
    icon: gptIcon,
  },
  {
    id: 'claude-sonnet-5.0-fast',
    displayName: 'Claude Sonnet 5.0',
    shortName: 'Claude Sonnet 5.0',
    description: 'Fast and capable',
    modelValue: import.meta.env.VITE_MODEL_CLAUDE_SONNET_5_0 || 'anthropic.claude-sonnet-5-0-v1:0',
    queryType: 'bedrock-api',
    activatesTool: null,
    icon: claudeIcon,
  },
  {
    id: 'claude-opus-4.8-reasoning',
    displayName: 'Claude Opus 4.8 (Reasoning)',
    shortName: 'Claude Opus 4.8',
    description: 'Solves complex problems',
    modelValue: import.meta.env.VITE_MODEL_CLAUDE_OPUS_4_8 || 'anthropic.claude-opus-4-8-v1:0',
    queryType: 'bedrock-api',
    activatesTool: 'reasoning',
    icon: claudeIcon,
  },
  {
    id: DEFAULT_IMAGE_MODEL_ID,
    displayName: 'GPT Image 1',
    shortName: 'GPT-Image-1',
    description: 'Standard quality',
    modelValue: import.meta.env.VITE_MODEL_GPT_IMAGE_1 || 'gpt-image-1',
    queryType: 'response-api',
    activatesTool: 'image_generation',
    icon: gptIcon,
  },
  {
    id: 'gpt-image-2',
    displayName: 'GPT Image 2 (beta)',
    shortName: 'GPT-Image-2 (beta)',
    description: 'High quality',
    modelValue: import.meta.env.VITE_MODEL_GPT_IMAGE_2 || 'gpt-5.5',
    queryType: 'response-api',
    activatesTool: 'image_generation',
    icon: gptIcon,
  },
  {
    id: WEB_SEARCH_MODEL_ID,
    displayName: 'Amazon Nova Lite',
    shortName: 'Amazon Nova Lite',
    modelValue: import.meta.env.VITE_MODEL_AMAZON_NOVA_LITE || 'gpt-5.5',
    queryType: 'bedrock-api',
    defaultForTool: 'web_search',
    visibleInSelector: false,
    icon: null,
  },
];

/**
 * Check if the model selector should be visible given the current tool and mode.
 *
 * **Visible** (returns `true`):
 *  - No tool selected (general chat, file upload summarization)
 *  - Reasoning tool
 *  - Image Generation tool
 *
 * **Hidden** (returns `false`):
 *  - Voice mode
 *  - Web Search tool
 *  - Data Analysis tool 
 *  - Document Translation tool
 *  - 3M Knowledge / RAG mode (handled externally by callers)
 */
export function isModelSelectorVisible(activeTool: string | null | undefined, isVoiceModeActive?: boolean): boolean {
  if (isVoiceModeActive) return false;
  return !activeTool || activeTool === 'reasoning' || activeTool === 'image_generation';
}

/**
 * Return the subset of MODEL_OPTIONS visible in the dropdown.
 * The selector always shows the same four user-facing options in standard chat and reasoning.
 */
export function getVisibleModelOptions(_activeTool: string | null | undefined): readonly ModelConfig[] {
  return MODEL_OPTIONS.filter(m => m.visibleInSelector !== false);
}

export function getToolForModelSelection(modelId: string): string | null {
  return MODEL_OPTIONS.find(m => m.id === modelId)?.activatesTool ?? null;
}

/**
 * Returns true when the given model id is compatible with (i.e. not hidden for) a specific tool.
 * Use this to check capability — prefer this over getVisibleModelOptions when the question
 * is "can this model be used with this tool?" rather than "what should the dropdown show?".
 *
 * Also returns the id of the best fallback model for the tool (first compatible entry),
 * so callers never need to hardcode a model id.
 *
 * Pass `null` for activeTool to check compatibility with general chat (no tool active).
 */
export function getToolCompatibility(modelId: string, tool: string | null): {
  compatible: boolean;
  fallbackId: string;
} {
  const compatible = tool === 'web_search'
    ? MODEL_OPTIONS
    : getVisibleModelOptions(tool);
  let fallbackId: string;
  if (tool === 'reasoning') {
    fallbackId = DEFAULT_REASONING_MODEL_ID;
  } else if (tool === 'image_generation') {
    fallbackId = DEFAULT_IMAGE_MODEL_ID;
  } else {
    fallbackId = DEFAULT_STANDARD_MODEL_ID;
  }

  return {
    compatible: compatible.some(m => m.id === modelId),
    fallbackId,
  };
}

/**
 * Returns true when the given model runs on the Bedrock (Claude) backend.
 * GPT models return false.
 *
 * Use this to gate features that are only available for GPT models (e.g. the new
 * blob-based file upload flow) until the Bedrock backend is ready.
 */
export function isBedrockModel(modelId: string): boolean {
  const model = MODEL_OPTIONS.find(m => m.id === modelId);
  return model?.queryType === 'bedrock-api';
}

/**
 * Tools for which model selection is applicable.
 * Only these tools (plus standard chat with no tool) send a model to the backend.
 * All other tools (data_analysis, document_translation, etc.) rely on
 * backend defaults — the request payload should NOT include a model override.
 */
export const MODEL_ELIGIBLE_TOOLS = new Set<string>(['reasoning', 'web_search', 'image_generation']);

/**
 * Resolve the effective model configuration given the selected model and active tool.
 *
 * Returns `null` when the active tool does not support client-side model selection
 * (e.g. data_analysis, document_translation). Callers should
 * omit the model/queryType fields from the request payload when `null` is returned.
 *
 * Centralizes all tool-dependent model overrides:
 * - Tool not eligible for model selection → returns null
 * - Tool with a defaultForTool model → uses that model automatically
 * - Selected model hidden for the active tool → falls back to first visible model
 * - Reasoning tool active → switches to reasoningModelValue when defined
 * - All other combinations use the model's default config
 */
export function resolveModelConfig(
  selectedModelId: string,
  activeTool?: string | null
): ResolvedModelConfig | null {
  // Model selection only applies to standard chat (no tool) and specific eligible tools.
  if (activeTool && !MODEL_ELIGIBLE_TOOLS.has(activeTool)) {
    return null;
  }

  // Check for a tool-specific default model (e.g. web_search → Amazon Nova)
  if (activeTool) {
    const toolDefault = MODEL_OPTIONS.find(m => m.defaultForTool === activeTool);
    if (toolDefault) {
      return { modelValue: toolDefault.modelValue, queryType: toolDefault.queryType };
    }
  }

  let fallbackId: string;
  if (activeTool === 'reasoning') {
    fallbackId = DEFAULT_REASONING_MODEL_ID;
  } else if (activeTool === 'image_generation') {
    fallbackId = DEFAULT_IMAGE_MODEL_ID;
  } else {
    fallbackId = DEFAULT_STANDARD_MODEL_ID;
  }

  const visibleModels = getVisibleModelOptions(activeTool);
  const model = visibleModels.find(m => m.id === selectedModelId)
    ?? MODEL_OPTIONS.find(m => m.id === fallbackId)
    ?? MODEL_OPTIONS[0];

  return {
    modelValue: model.modelValue,
    queryType: model.queryType,
  };
}
